import React from 'react';
import {
  Box,
  Heading,
  Button,
  useToast,
  HStack,
  Flex,
  useDisclosure,
  Divider,
  ButtonGroup,
  IconButton,
  Tooltip,
  useColorModeValue
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import KanbanBoard from '../components/leads/KanbanBoard';
import NewLeadModal from '../components/leads/NewLeadModal';
import { AddIcon, ViewIcon, ChevronUpIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import ActionableMetrics from '../components/leads/ActionableMetrics';
import FontSizeControls from '../components/common/FontSizeControls';
import ColorModeToggle from '../components/common/ColorModeToggle';
import { useFontSize } from '../contexts/FontSizeContext';

const HomePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const kanbanRef = React.useRef();
  const [activeMetric, setActiveMetric] = React.useState(null);
  const { fontScale } = useFontSize();
  const bgColor = useColorModeValue('white', 'gray.800');
  const greenGreyBg = useColorModeValue('custom.greenGrey.light', 'custom.greenGrey.dark');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Logout Error",
        description: error.message || "Failed to log out",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleLeadAdded = async (lead) => {
    if (kanbanRef.current?.refreshLeads) {
      await kanbanRef.current.refreshLeads();
    }
    
    toast({
      title: 'Success',
      description: `Lead ${lead.leadId} has been ${lead.isNew ? 'created' : 'updated'} successfully`,
      status: 'success',
      duration: 3000
    });
  };

  return (
    <Box minH="100vh" bg={greenGreyBg}>
      <Box py={3} px={4} bg={bgColor} shadow="sm">
        <Flex justify="space-between" align="center" gap={4}>
          <HStack spacing={4} flex="1">
            <Box width="120px" height="32px">
              <img src="/logo.png" alt="Sumaaroh" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            <Heading size="md">Lead Management (Old Kanban)</Heading>
          </HStack>
          
          <Box flex="2">
            <HStack spacing={4} justify="center">
              <ActionableMetrics 
                onMetricClick={(metricType) => {
                  if (kanbanRef.current?.filterByMetric) {
                    kanbanRef.current.filterByMetric(metricType);
                    setActiveMetric(metricType);
                  }
                }}
                activeMetric={activeMetric}
              />
              <Divider orientation="vertical" height="20px" />
              <FontSizeControls />
              <ColorModeToggle />
            </HStack>
          </Box>

          <ButtonGroup size="sm" spacing={2} flex="1" justifyContent="flex-end">
            <Button
              leftIcon={<ViewIcon />}
              variant="ghost"
              onClick={() => navigate('/sheets')}
              size="sm"
            >
              Lead Sync
            </Button>
            <Button
              leftIcon={<ExternalLinkIcon />}
              variant="ghost"
              onClick={() => navigate('/')}
              size="sm"
              colorScheme="teal"
            >
              Go to Main Kanban
            </Button>
            <Button
              leftIcon={<ChevronUpIcon />}
              variant="ghost"
              onClick={() => navigate('/analytics')}
              size="sm"
            >
              View Analytics
            </Button>
            <Divider orientation="vertical" />
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={onOpen}
              size="sm"
            >
              Add Lead
            </Button>
            <Button
              onClick={handleLogout}
              colorScheme="red"
              variant="ghost"
              size="sm"
            >
              Logout
            </Button>
          </ButtonGroup>
        </Flex>
      </Box>
      
      <Box p={6} style={{ fontSize: `${fontScale}rem` }}>
        <KanbanBoard ref={kanbanRef} />
      </Box>

      <NewLeadModal
        isOpen={isOpen}
        onClose={onClose}
        onLeadAdded={handleLeadAdded}
      />
    </Box>
  );
};

export default HomePage;
